/**
 * AccessibleLiveRegion Component
 * Provides a reusable live region for screen reader announcements
 */

import { useEffect, useRef } from 'react';
import { SR_ONLY_CLASS } from '@/utils/accessibility';

export interface AccessibleLiveRegionProps {
  /**
   * The message to announce
   */
  message: string | null;

  /**
   * If true, uses aria-live="assertive" for urgent announcements
   * If false, uses aria-live="polite" for non-urgent announcements
   */
  assertive?: boolean;

  /**
   * Optional role for the region
   * Defaults to 'status' for polite regions, 'alert' for assertive regions
   */
  role?: string;

  /**
   * Optional ID for the region (useful for aria-describedby references)
   */
  id?: string;
}

/**
 * AccessibleLiveRegion - Announces messages to screen readers
 * 
 * @example
 * ```tsx
 * const [announcement, setAnnouncement] = useState<string | null>(null);
 * 
 * return (
 *   <>
 *     <button onClick={() => setAnnouncement('Data loaded successfully')}>
 *       Load Data
 *     </button>
 *     <AccessibleLiveRegion message={announcement} assertive={false} />
 *   </>
 * );
 * ```
 */
export function AccessibleLiveRegion({
  message,
  assertive = false,
  role,
  id,
}: AccessibleLiveRegionProps): JSX.Element {
  const regionRef = useRef<HTMLDivElement>(null);

  // Default roles based on assertive prop
  const defaultRole = assertive ? 'alert' : 'status';
  const finalRole = role || defaultRole;

  useEffect(() => {
    if (!message) return;

    // For NVDA and JAWS compatibility, clear and re-add content
    if (regionRef.current) {
      regionRef.current.textContent = '';

      // Use setTimeout to allow screen reader to process the change
      setTimeout(() => {
        if (regionRef.current) {
          regionRef.current.textContent = message;
        }
      }, 100);
    }
  }, [message]);

  return (
    <div
      ref={regionRef}
      id={id}
      role={finalRole}
      aria-live={assertive ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={SR_ONLY_CLASS}
      data-testid="accessible-live-region"
    />
  );
}

export default AccessibleLiveRegion;
